import React, { useState } from 'react';
import { 
  Box, CssBaseline, Drawer, IconButton, List, ListItem, ListItemButton, 
  ListItemIcon, ListItemText, Toolbar, Typography, Avatar, Menu, MenuItem, 
  useMediaQuery, useTheme, Divider, Tooltip, Dialog, DialogContent
} from '@mui/material';
import { 
  Menu as MenuIcon, HomeRounded, AccountBalanceWalletRounded, 
  MoneyOffRounded, TrendingUpRounded, Close, Security, SwapHorizRounded
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import Perfil from './Perfil'; // Importar o Perfil
import BillingDialog from './BillingDialog';

const drawerWidth = 240;
const collapsedWidth = 75;

function Layout({ children, user }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false); 
  const [anchorEl, setAnchorEl] = useState(null);
  const [perfilOpen, setPerfilOpen] = useState(false); // Estado para o modal
  const [billingOpen, setBillingOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const handleLogout = async () => {
    try {
      const now = new Date();
      await updateDoc(doc(db, 'users', user.uid), {
        horarioSaida: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        ultimoLogoutEm: now.toISOString(),
        atualizadoEm: now.toISOString()
      });
      await addDoc(collection(db, `users/${user.uid}/logs`), {
        tipo: 'logout',
        acao: 'Saida do sistema',
        data: now.toISOString(),
        origem: 'manual'
      });
    } catch (error) {
      console.warn('Nao foi possivel registrar logout:', error);
    } finally {
      auth.signOut();
      navigate('/');
    }
  };
  
  // Handlers do modal
  const handleOpenPerfil = () => setPerfilOpen(true);
  const handleClosePerfil = () => setPerfilOpen(false);
  const handleOpenBilling = () => setBillingOpen(true);
  const handleCloseBilling = () => setBillingOpen(false);

  const menuItems = [
    { text: 'Visão Geral', icon: <HomeRounded />, path: '/' },
    { text: 'Bancos', icon: <AccountBalanceWalletRounded />, path: '/bancos' },
    { text: 'Despesas', icon: <MoneyOffRounded />, path: '/gastos' },
    { text: 'Caixinha', icon: <TrendingUpRounded />, path: '/investimentos' },
    { text: 'Empréstimos', icon: <SwapHorizRounded />, path: '/emprestimos' },
  ];

  const isAdmin = user?.email === 'lucas.nogueira.teixeira18@gmail.com';
  if (isAdmin) {
    menuItems.push({ text: 'Admin', icon: <Security />, path: '/admin' });
  }

  const drawerContent = (
    <Box 
      sx={{ height: '100%', background: '#0a1929', color: 'white', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}
      onMouseEnter={() => !isMobile && setHoverOpen(true)}
      onMouseLeave={() => !isMobile && setHoverOpen(false)}
    >
      <Toolbar sx={{ px: 1, justifyContent: 'center', minHeight: { xs: 56, sm: 64 } }}>
        <Typography variant="h6" fontWeight="bold" color="#00e676" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
          {(hoverOpen || isMobile) ? 'LNT Tech Finance' : 'LNT'}
        </Typography>
      </Toolbar>
      
      <List sx={{ px: 1, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <Tooltip title={!hoverOpen && !isMobile ? item.text : ""} placement="right">
                <ListItemButton 
                  onClick={() => { navigate(item.path); if(isMobile) setMobileOpen(false); }}
                  sx={{ 
                    borderRadius: 2, justifyContent: (hoverOpen || isMobile) ? 'initial' : 'center', px: 2.5,
                    bgcolor: active ? 'rgba(0, 230, 118, 0.15)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: (hoverOpen || isMobile) ? 3 : 'auto', justifyContent: 'center', color: active ? '#00e676' : '#94a3b8' }}>
                    {item.icon}
                  </ListItemIcon>
                  {(hoverOpen || isMobile) && <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: active ? 'bold' : 'normal' }} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )
        })}
      </List>
      
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      <Box sx={{ p: 1, mb: 1 }}>
        <ListItemButton onClick={handleProfileMenu} sx={{ borderRadius: 2, justifyContent: (hoverOpen || isMobile) ? 'initial' : 'center' }}>
          <Avatar sx={{ width: 35, height: 35, mr: (hoverOpen || isMobile) ? 1.5 : 0, bgcolor: '#00e676', color: '#0a1929', fontWeight: 'bold' }}>
            {user?.email?.[0].toUpperCase()}
          </Avatar>
          {(hoverOpen || isMobile) && <ListItemText primary="Meu Perfil" secondary={user?.email} primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} secondaryTypographyProps={{ fontSize: '0.65rem', color: '#94a3b8', noWrap: true }} />}
        </ListItemButton>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} PaperProps={{ sx: { bgcolor: '#1e293b', color: 'white', ml: 1 } }}>
        <MenuItem onClick={() => { handleOpenPerfil(); handleCloseMenu(); }}>Configurações</MenuItem>
        <MenuItem onClick={() => { handleOpenBilling(); handleCloseMenu(); }}>Assinatura e Pagamento</MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: '#ff5252' }}>Sair do Sistema</MenuItem>
      </Menu>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {isMobile && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, p: 1, height: 60, display: 'flex', alignItems: 'center', zIndex: 1201, background: 'rgba(240, 242, 245, 0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #ddd' }}>
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#0a1929' }}><MenuIcon /></IconButton>
          <Typography variant="body1" fontWeight="bold" sx={{ ml: 1 }}>LNT Tech</Typography>
        </Box>
      )}
      
      <Box 
        component="nav" 
        sx={{ width: { sm: hoverOpen ? drawerWidth : collapsedWidth }, flexShrink: { sm: 0 }, transition: 'width 0.3s ease' }}
      >
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, bgcolor: '#0a1929' } }}>
          {drawerContent}
        </Drawer>
        <Drawer 
          variant="permanent" 
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: hoverOpen ? drawerWidth : collapsedWidth, overflowX: 'hidden', transition: 'width 0.3s ease', border: 'none', bgcolor: '#0a1929' } }} 
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 4 }, mt: { xs: 8, sm: 0 }, minHeight: '100vh', bgcolor: '#f0f2f5', width: '100%', overflowX: 'hidden' }}>
        {children}
      </Box>

      {/* MODAL DE PERFIL */}
      <Dialog 
        open={perfilOpen} 
        onClose={handleClosePerfil}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogContent sx={{ position: 'relative', p: { xs: 2, sm: 3 } }}>
           <IconButton
            aria-label="close"
            onClick={handleClosePerfil}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
          <Perfil user={user} />
        </DialogContent>
      </Dialog>

      <BillingDialog open={billingOpen} onClose={handleCloseBilling} user={user} />
    </Box>
  );
}

export default Layout;
